import { z } from "zod";
import { TermMetaSchema } from "../schemas";

export const CLIENT_STATUSES = ["ACTIVE", "INACTIVE"] as const;

const AddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});

const EmailSchema = z.object({
  name: z.string(),
  address: z.string().email(),
});

export const ClientMetaSchema = z.object({
  terms: TermMetaSchema.optional(),
  address: AddressSchema.optional(),
  emails: z.array(EmailSchema).optional(),
  phone: z.string().optional(),
  industry: z.string().optional(),
  markets: z.array(z.string()).optional(),
  sage_id: z.string().optional(),
  locations: z.array(z.any()).optional(),
});

export type ClientEmailType = z.infer<typeof EmailSchema>;

export const ClientStatusSchema = z.enum(CLIENT_STATUSES).default("ACTIVE");

export type ClientStatusType = z.infer<typeof ClientStatusSchema>;

export type ClientMetaType = z.infer<typeof ClientMetaSchema>;

/** JSON value stored in a `jsonb` column. */
type Json = string | number | boolean | null | { [key: string]: Json } | Json[];

/**
 * Standard columns of `public.clients` — hand-authored (replaces the snaplet
 * `public_clientsScalars`). Typed as the row is READ from the DB: NOT NULL columns
 * are non-null; nullable columns include `null`. Snaplet marked default-bearing
 * columns (e.g. `id`, `created_at`) optional, which was wrong for read paths.
 */
export type ClientColumns = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  status: string;
  addresses: Json;
  archived: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  meta_data: Json;
};

export type ClientType = Omit<ClientColumns, "status" | "meta_data"> & {
  status: ClientStatusType;
  meta_data?: ClientMetaType;
  // Link table relationships
  client_abilities?: {
    ability: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  client_restrictions?: {
    restriction: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  client_locations?: {
    location: {
      id: string;
      name: string;
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      description?: string;
      instructions?: string;
    };
  }[];
  client_banned_users?: { user: { id: string }; reason?: string }[];
  client_preferred_users?: { user: { id: string }; reason?: string }[];
  client_contacts?: { contact: { id: string }; note?: string }[];
};
